import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useLocation } from 'react-router-dom'
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '@/context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

const isLocalAuthMode = import.meta.env.VITE_AUTH_MODE === 'local'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

const forgotSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type ForgotFormData = z.infer<typeof forgotSchema>

// passwordPolicy mirrors the Cognito user pool PasswordPolicy in
// infra/stacks/auth.go: min 8 chars, at least one upper, one lower, one
// digit. Symbols are optional. Validating client-side lets us surface
// specific messages before Cognito rejects the challenge.
const passwordPolicy = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/\d/, 'Password must contain a number')

const resetSchema = z
  .object({
    code: z.string().min(1, 'Confirmation code is required'),
    newPassword: passwordPolicy,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetFormData = z.infer<typeof resetSchema>

const newPasswordSchema = z
  .object({
    newPassword: passwordPolicy,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type NewPasswordFormData = z.infer<typeof newPasswordSchema>

const totpCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
})

type TotpCodeFormData = z.infer<typeof totpCodeSchema>

type FlowState = 'login' | 'forgot' | 'reset' | 'newPassword' | 'totpCode' | 'totpSetup'

/**
 * Map Cognito error codes to user-friendly messages.
 */
function getCognitoErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.toLowerCase()

    if (message.includes('user does not exist') || message.includes('incorrect username')) {
      return 'Invalid email or password'
    }
    if (message.includes('incorrect password') || message.includes('not authorized')) {
      return 'Invalid email or password'
    }
    if (message.includes('user is not confirmed')) {
      return 'Please verify your email address before signing in'
    }
    if (message.includes('password reset required')) {
      return 'Password reset is required. Please contact your administrator.'
    }
    if (message.includes('too many requests') || message.includes('attempt limit exceeded')) {
      return 'Too many failed attempts. Please try again later.'
    }
    if (message.includes('network')) {
      return 'Network error. Please check your connection and try again.'
    }
    if (message.includes('invalid verification code') || message.includes('invalid code')) {
      return 'Invalid confirmation code. Please check and try again.'
    }
    if (
      message.includes('software token') ||
      message.includes('enablesoftwaretokenmfa') ||
      message.includes('verifysoftwaretoken') ||
      message.includes('invalid mfa')
    ) {
      return 'That code is incorrect or has expired. Try again with a fresh code from your authenticator app.'
    }
    if (message.includes('expired') && message.includes('code')) {
      return 'Confirmation code has expired. Please request a new one.'
    }
    if (message.includes('limit exceeded')) {
      return 'Too many attempts. Please try again later.'
    }

    return error.message
  }

  return 'An unexpected error occurred. Please try again.'
}

function ForgotPasswordForm({
  onCodeSent,
  onBack,
}: {
  onCodeSent: (email: string) => void
  onBack: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  })

  const onSubmit = async (data: ForgotFormData) => {
    setError(null)
    setIsSubmitting(true)

    try {
      await resetPassword({ username: data.email })
      onCodeSent(data.email)
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Forgot password</CardTitle>
        <CardDescription>
          Enter your email address and we'll send you a confirmation code
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              placeholder="name@company.com"
              autoComplete="email"
              disabled={isSubmitting}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Sending code...' : 'Send reset code'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function ResetPasswordForm({
  email,
  onSuccess,
  onBack,
}: {
  email: string
  onSuccess: () => void
  onBack: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  })

  const onSubmit = async (data: ResetFormData) => {
    setError(null)
    setIsSubmitting(true)

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: data.code,
        newPassword: data.newPassword,
      })
      onSuccess()
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Reset password</CardTitle>
        <CardDescription>
          Enter the confirmation code sent to {email} and your new password
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="code">Confirmation code</Label>
            <Input
              id="code"
              type="text"
              placeholder="Enter code"
              autoComplete="one-time-code"
              disabled={isSubmitting}
              {...register('code')}
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              {...register('newPassword')}
            />
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Resetting password...' : 'Reset password'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Shown after a successful temp-password sign-in when Cognito returns the
 * CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED challenge (admin-invited
 * users on first login). On success the user is fully signed in and the
 * caller's onSuccess handler navigates them into the app.
 *
 * No "back to sign in" action: the Amplify challenge is tied to the
 * current sign-in attempt and can't be resumed once dismissed. The only
 * cancel path is reloading the page, which discards the challenge.
 */
function SetNewPasswordForm({ onSuccess }: { onSuccess: () => void }) {
  const { completeNewPassword } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
  })

  const onSubmit = async (data: NewPasswordFormData) => {
    setError(null)
    setIsSubmitting(true)

    try {
      await completeNewPassword(data.newPassword)
      onSuccess()
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Set a new password</CardTitle>
        <CardDescription>
          Your account requires a new password before you can continue.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-0.5">
            <li>At least 8 characters</li>
            <li>One uppercase and one lowercase letter</li>
            <li>One number</li>
          </ul>

          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              {...register('newPassword')}
            />
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password-confirm">Confirm new password</Label>
            <Input
              id="new-password-confirm"
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Setting password...' : 'Set password and sign in'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Shown when Cognito returns CONFIRM_SIGN_IN_WITH_TOTP_CODE — the user has
 * TOTP MFA enabled on their account and must supply a 6-digit code from
 * their authenticator app to finish signing in.
 */
function TotpCodeForm({ onSuccess }: { onSuccess: () => void }) {
  const { confirmTotpCode } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TotpCodeFormData>({
    resolver: zodResolver(totpCodeSchema),
  })

  const onSubmit = async (data: TotpCodeFormData) => {
    setError(null)
    setIsSubmitting(true)

    try {
      await confirmTotpCode(data.code)
      onSuccess()
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Two-factor authentication</CardTitle>
        <CardDescription>
          Enter the 6-digit code from your authenticator app to finish signing in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="totp-code">Verification code</Label>
            <Input
              id="totp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              disabled={isSubmitting}
              {...register('code')}
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying...' : 'Verify and sign in'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Shown when Cognito returns CONTINUE_SIGN_IN_WITH_TOTP_SETUP — the user
 * is required to enroll in TOTP before sign-in completes. Displays a QR
 * code for the otpauth:// URI (plus the shared secret for manual entry)
 * and asks for a verification code from the authenticator app.
 */
function TotpSetupForm({ onSuccess }: { onSuccess: () => void }) {
  const { confirmTotpCode, totpSetupDetails } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TotpCodeFormData>({
    resolver: zodResolver(totpCodeSchema),
  })

  const onSubmit = async (data: TotpCodeFormData) => {
    setError(null)
    setIsSubmitting(true)

    try {
      await confirmTotpCode(data.code)
      onSuccess()
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!totpSetupDetails) {
    // Defensive: a TOTP-setup challenge was requested but the setup
    // payload was lost (e.g. a hard reload). Fail visibly rather than
    // show an empty QR code; the user can restart the sign-in flow.
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Set up two-factor authentication</CardTitle>
          <CardDescription>
            We lost the enrollment details for this session. Please reload and sign in again.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Set up two-factor authentication</CardTitle>
        <CardDescription>
          Scan the QR code with an authenticator app (Authy, 1Password, Google
          Authenticator), then enter the 6-digit code it shows.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          <div className="flex flex-col items-center gap-3">
            <div className="rounded-lg border bg-white p-4">
              <QRCodeSVG value={totpSetupDetails.setupUri} size={180} level="M" />
            </div>
            <div className="w-full">
              <Label className="text-xs text-muted-foreground">
                Or enter this secret manually
              </Label>
              <p className="mt-1 font-mono text-xs break-all bg-muted p-2 rounded">
                {totpSetupDetails.sharedSecret}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="totp-setup-code">Verification code</Label>
            <Input
              id="totp-setup-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              disabled={isSubmitting}
              {...register('code')}
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying...' : 'Verify and finish sign in'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export function LoginPage() {
  const { login, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [flowState, setFlowState] = useState<FlowState>('login')
  const [resetEmail, setResetEmail] = useState('')

  // Get the redirect path from location state, or default to home
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, from])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setError(null)
    setSuccessMessage(null)
    setIsSubmitting(true)

    try {
      const result = await login(data.email, data.password)
      if (result === 'new_password_required') {
        setFlowState('newPassword')
        return
      }
      if (result === 'totp_required') {
        setFlowState('totpCode')
        return
      }
      if (result === 'totp_setup_required') {
        setFlowState('totpSetup')
        return
      }
      navigate(from, { replace: true })
    } catch (err) {
      setError(getCognitoErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackToLogin = () => {
    setFlowState('login')
    setError(null)
  }

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (flowState === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <ForgotPasswordForm
          onCodeSent={(email) => {
            setResetEmail(email)
            setFlowState('reset')
          }}
          onBack={handleBackToLogin}
        />
      </div>
    )
  }

  if (flowState === 'reset') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <ResetPasswordForm
          email={resetEmail}
          onSuccess={() => {
            setFlowState('login')
            setSuccessMessage('Password reset successfully. Please sign in.')
          }}
          onBack={handleBackToLogin}
        />
      </div>
    )
  }

  if (flowState === 'newPassword') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <SetNewPasswordForm
          onSuccess={() => {
            navigate(from, { replace: true })
          }}
        />
      </div>
    )
  }

  if (flowState === 'totpCode') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <TotpCodeForm
          onSuccess={() => {
            navigate(from, { replace: true })
          }}
        />
      </div>
    )
  }

  if (flowState === 'totpSetup') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <TotpSetupForm
          onSuccess={() => {
            navigate(from, { replace: true })
          }}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
          <CardDescription>
            Enter your email and password to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {successMessage && (
              <div className="p-3 text-sm text-green-700 bg-green-50 rounded-md dark:text-green-400 dark:bg-green-950/30">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                autoComplete="email"
                disabled={isSubmitting}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                disabled={isSubmitting}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {!isLocalAuthMode && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setFlowState('forgot')
                    setError(null)
                    setSuccessMessage(null)
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
